import { Facebook, Instagram, Globe, Phone, Mail, MapPin, Award } from "lucide-react";
import Logo from "./Logo";
import { CONTACT } from "../data/content";

const COLUMNS = [
  {
    title: "COMPANY",
    links: ["About Us", "Our Science", "Quality Assurance", "Careers"],
  },
  {
    title: "CUSTOMER CARE",
    links: ["Contact Us", "FAQs", "Terms & Conditions", "Privacy Policy"],
  },
];

export default function Footer() {
  return (
    <footer data-testid="footer" className="bg-fv-navy text-white">
      <div className="mx-auto max-w-7xl px-5 pb-10 pt-20 lg:px-8 lg:pt-28">
        <div className="grid grid-cols-1 gap-12 md:grid-cols-2 lg:grid-cols-4">
          <div>
            <Logo dark />
            <p className="mt-6 max-w-xs text-sm leading-relaxed text-white/60">
              FellesViar is a 4-in-1 joint support supplement developed based
              on scientific research to help you move better, every day.
            </p>
            <div className="mt-7 flex gap-3" data-testid="social-links">
              {[
                { icon: Facebook, label: "Facebook", url: "https://www.facebook.com/aspennutritionmalaysia" },
                { icon: Instagram, label: "Instagram", url: "https://www.instagram.com/aspen.nutrition/" },
                { icon: Globe, label: "Website", url: "https://www.aspensarl.com/" },
              ].map(({ icon: Icon, label, url }) => (
                <a
                  key={label}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  data-testid={`social-${label.toLowerCase()}`}
                  className="flex h-10 w-10 items-center justify-center rounded-full border border-white/15 text-white/70 transition-all duration-300 hover:-translate-y-1 hover:border-fv-cyan hover:bg-fv-cyan hover:text-fv-navy"
                >
                  <Icon className="h-4 w-4" />
                </a>
              ))}
            </div>
          </div>

          {COLUMNS.map((col) => (
            <div key={col.title}>
              <h4 className="text-[11px] font-bold tracking-[0.25em] text-fv-cyan">
                {col.title}
              </h4>
              <ul className="mt-6 space-y-3.5">
                {col.links.map((l) => (
                  <li key={l}>
                    <a
                      href="#"
                      onClick={(e) => e.preventDefault()}
                      data-testid={`footer-link-${l.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                      className="text-sm text-white/60 transition-colors duration-300 hover:text-white"
                    >
                      {l}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}

          <div>
            <h4 className="text-[11px] font-bold tracking-[0.25em] text-fv-cyan">
              CONTACT US
            </h4>
            <div className="mt-6 space-y-4" data-testid="footer-contact">
              <div className="font-display text-sm font-bold text-white">
                {CONTACT.company}
              </div>
              {CONTACT.phones.map((p) => (
                <a
                  key={p}
                  href={`tel:+6${p.replace(/[^\d]/g, "")}`}
                  data-testid={`footer-phone-${p.replace(/[^\d]/g, "")}`}
                  className="flex items-center gap-3 text-sm text-white/60 transition-colors hover:text-white"
                >
                  <Phone className="h-4 w-4 shrink-0 text-fv-cyan" /> {p}
                </a>
              ))}
              <a href={`mailto:${CONTACT.email}`} data-testid="footer-email" className="flex items-center gap-3 text-sm text-white/60 transition-colors hover:text-white">
                <Mail className="h-4 w-4 shrink-0 text-fv-cyan" /> {CONTACT.email}
              </a>
              <div className="flex items-start gap-3 text-sm leading-relaxed text-white/60" data-testid="footer-address">
                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-fv-cyan" /> {CONTACT.address}
              </div>
            </div>
          </div>
        </div>

        <div className="mt-16 flex flex-col items-start justify-between gap-6 border-t border-white/10 pt-8 sm:flex-row sm:items-center">
          <p className="text-xs text-white/40" data-testid="footer-copyright">
            © 2026 Aspen Sarl Sdn Bhd. All rights reserved.
          </p>
          <div className="flex items-center gap-4" data-testid="certification-marks">
            <a
              href="/assets/halal-certificate.png"
              target="_blank"
              rel="noopener noreferrer"
              title="View Halal certificate"
              data-testid="footer-halal-cert-link"
              className="flex h-16 w-16 items-center justify-center overflow-hidden rounded-full border-2 border-white/30 bg-white transition-transform duration-300 hover:-translate-y-1"
            >
              <img
                src="/assets/halal-mark.png"
                alt="Halal certified — view certificate"
                className="h-12 w-12 object-contain"
              />
            </a>
            <div className="halal-badge h-14 w-14 text-fv-cyan">
              <Award className="h-4 w-4" />
              <span className="mt-0.5 text-[7px] font-extrabold tracking-widest">GMP</span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
